import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { MessageCircleQuestion, Send, HelpCircle } from "lucide-react";
import { fmtDate } from "../lib/format";

const inputCls = "w-full px-4 py-3 rounded-xl border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30";

export default function SoruCevap() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", question: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/questions").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/questions", form);
      setSent(true);
      setForm({ name: "", email: "", question: "" });
      toast.success("Sorunuz alındı — yanıtlanınca bu sayfada yayınlanacak");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Soru gönderilemedi");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="soru-cevap-page" className="max-w-6xl mx-auto px-4 py-10 sm:py-14">
      <div className="mb-10">
        <h1 className="font-serif text-3xl sm:text-4xl text-forest">Soru & Cevap</h1>
        <p className="text-stone-500 text-sm mt-2">Ürünlerimiz, sipariş, kargo veya ödeme hakkında merak ettiklerinizi sorun. Yanıtlanan sorular bu sayfada herkese açık yayınlanır.</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-8 items-start">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
          <h2 className="font-semibold text-stone-900 flex items-center gap-2 mb-4"><MessageCircleQuestion className="w-5 h-5 text-forest" /> Soru Sor</h2>
          {sent ? (
            <div data-testid="qa-general-success" className="text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              Sorunuz alındı — yanıtlandığında bu sayfada yayınlanacak.
              <button data-testid="qa-general-again" onClick={() => setSent(false)} className="block mt-3 text-xs font-bold text-forest underline">Yeni soru sor</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <input data-testid="qa-general-name" required className={inputCls} placeholder="Adınız *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input data-testid="qa-general-email" type="email" className={inputCls} placeholder="E-posta (isteğe bağlı)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <textarea data-testid="qa-general-question" required rows={4} className={inputCls} placeholder="Sorunuz *" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} />
              <button data-testid="qa-general-submit" disabled={busy} className="w-full flex items-center justify-center gap-2 bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white text-sm font-bold px-5 py-3 rounded-xl transition-colors">
                <Send className="w-4 h-4" /> {busy ? "Gönderiliyor..." : "Soruyu Gönder"}
              </button>
            </form>
          )}
          <p className="text-xs text-stone-400 mt-4 leading-relaxed">Belirli bir ürün hakkında soru sormak için ürün sayfasındaki "Soru & Cevap" sekmesini de kullanabilirsiniz.</p>
        </div>

        <div className="lg:col-span-3 space-y-3" data-testid="qa-general-list">
          <h2 className="font-semibold text-stone-900 mb-1">Yanıtlanan Sorular ({items.length})</h2>
          {items.length === 0 && (
            <div className="bg-white rounded-2xl border border-stone-200/80 p-10 text-center text-stone-400 text-sm">
              <HelpCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />Henüz yanıtlanmış soru yok — ilk soruyu siz sorun.
            </div>
          )}
          {items.map((q) => (
            <div key={q.question_id} data-testid={`qa-general-item-${q.question_id}`} className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm">
              <p className="text-xs text-stone-400">{q.name} · {fmtDate(q.created_at)}</p>
              <p className="mt-1.5 text-sm font-semibold text-stone-800">{q.question}</p>
              <p className="mt-2 text-sm text-stone-600 bg-cream-dark/60 rounded-xl p-3"><span className="font-semibold text-forest">Yanıt:</span> {q.answer}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
